const labels: Readonly<Record<string, string>> = {
  'individual.first_name': 'Nome',
  'individual.last_name': 'Sobrenome',
  'individual.id_number': 'CPF ou CNPJ',
  'individual.document_number': 'CPF ou CNPJ',
  'individual.phone': 'Telefone',
  'individual.email': 'E-mail',
  'individual.political_exposure': 'Pessoa politicamente exposta (PEP)',
  'individual.dob.day': 'Data de nascimento',
  'individual.dob.month': 'Data de nascimento',
  'individual.dob.year': 'Data de nascimento',
  'individual.address.line1': 'Endereço',
  'individual.address.line2': 'Complemento',
  'individual.address.city': 'Cidade',
  'individual.address.state': 'Estado',
  'individual.address.postal_code': 'CEP',
  'individual.address.country': 'País',
  'company.name': 'Razão social',
  'company.tax_id': 'CPF ou CNPJ',
  'company.phone': 'Telefone da empresa',
  'company.address.line1': 'Endereço da empresa',
  'company.address.line2': 'Complemento da empresa',
  'company.address.city': 'Cidade da empresa',
  'company.address.state': 'Estado da empresa',
  'company.address.postal_code': 'CEP da empresa',
  'company.address.country': 'País da empresa',
  'business_profile.name': 'Nome fantasia',
  'business_profile.url': 'Site da empresa',
  'business_profile.product_description': 'Descrição do negócio',
  'business_profile.mcc': 'Categoria do negócio',
  'representative.id_number': 'CPF do representante legal',
  'representative.relationship.title': 'Cargo do representante legal',
  'representative.first_name': 'Nome do representante legal',
  'representative.last_name': 'Sobrenome do representante legal',
  'representative.dob.day': 'Data de nascimento do representante legal',
  'representative.dob.month': 'Data de nascimento do representante legal',
  'representative.dob.year': 'Data de nascimento do representante legal',
  'representative.phone': 'Telefone do representante legal',
  'representative.political_exposure': 'Pessoa politicamente exposta (PEP)',
  'representative.email': 'E-mail do representante legal',
  'representative.address.line1': 'Endereço do representante legal',
  'representative.address.line2': 'Complemento do representante legal',
  'representative.address.city': 'Cidade do representante legal',
  'representative.address.state': 'Estado do representante legal',
  'representative.address.postal_code': 'CEP do representante legal',
  'representative.address.country': 'País do representante legal',
}

export type RequirementCategory = 'form' | 'embedded' | 'support' | 'bank_account'

function normalizeStripePath(path: string): string {
  return path.replaceAll('[', '.').replaceAll(']', '')
}

export function isRepresentativeAddressRequirement(path: string): boolean {
  return /^(?:representative|person_[^.]+)\.address\./.test(normalizeStripePath(path))
}

function getCategorySpecificLabel(path: string, category: RequirementCategory): string | undefined {
  if (category === 'form') {
    const personField = path.match(/^person_[^.]+\.(.+)$/)?.[1]
    return personField ? labels[`representative.${personField}`] : undefined
  }

  const isPersonPath = /^person_[^.]+\./.test(path)
  if (category !== 'embedded') {
    return isPersonPath ? 'Dados de uma pessoa vinculada à empresa' : undefined
  }

  if (path === 'company.verification.document') return 'Documento da empresa'
  if (
    path === 'company.owners_provided' ||
    path === 'owners_provided' ||
    path.startsWith('owners.') ||
    path.startsWith('company.owners.')
  ) {
    return 'Sócios da empresa'
  }
  if (path.endsWith('.verification.additional_document')) return 'Documento adicional'
  if (path.endsWith('.verification.document')) return 'Documento de identificação'
  if (isPersonPath) return 'Dados de uma pessoa vinculada à empresa'

  return undefined
}

export function getRequirementFieldLabel(
  path: string | null | undefined,
  category?: RequirementCategory,
): string | undefined {
  if (!path) return undefined
  const normalizedPath = normalizeStripePath(path)
  return (
    (category ? getCategorySpecificLabel(normalizedPath, category) : undefined) ??
    labels[normalizedPath]
  )
}

/**
 * Label for a summary that names one requirement: Stripe verifies the address as a whole and flags
 * every part at once, so any address part reads as the address.
 */
export function getRequirementSummaryLabel(
  path: string | null | undefined,
  category?: RequirementCategory,
): string | undefined {
  if (!path) return undefined
  const addressPath = normalizeStripePath(path).replace(
    /\.address\.(?:line1|line2|city|state|postal_code|country)$/,
    '.address.line1',
  )
  return getRequirementFieldLabel(addressPath, category)
}

export function getSafeRequirementLabel(
  path: string | null | undefined,
  category?: RequirementCategory,
): string {
  return getRequirementFieldLabel(path, category) ?? 'um dado do cadastro'
}
